import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../Assets/assetsShared";
import "./CreateMailAccount.css";

// This page works only for the softvolute.in mail domain
const DOMAIN = "softvolute.in";
const MAILBOX_REGEX = /^[a-z0-9][a-z0-9._-]{0,63}$/;

const EMPTY_FORM = {
  name: "",
  username: "",
  password: "",
  confirmPassword: "",
};

function mailError(error) {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.status === 401) {
    return "Session expired. Please log in again.";
  }

  if (error?.response?.status === 403) {
    return "Access denied.";
  }

  if (error?.response?.status === 404) {
    return "This function is not available in the backend yet.";
  }

  return error?.message || "Request failed.";
}

// returns an error text, or "" when the password is valid
function passwordProblem(password) {
  if (password.length < 1 || password.length > 30) {
    return "Password can be at most 30 characters.";
  }

  if (
    !/[A-Z]/.test(password) ||
    !/[a-z]/.test(password) ||
    !/[0-9]/.test(password) ||
    !/[^A-Za-z0-9]/.test(password)
  ) {
    return "Password must include uppercase, lowercase, number and special character.";
  }

  return "";
}

function normalizeAccount(item) {
  const email = String(item.email || item.emailAddress || "")
    .trim()
    .toLowerCase();

  const status = String(item.status || "").toLowerCase();

  return {
    name: item.name || item.employeeName || "—",
    email,
    erpLinked: item.erpLinked === true,
    status:
      item.enabled === false ||
      item.disabled === true ||
      ["disabled", "inactive", "0"].includes(status)
        ? "Disabled"
        : ["active", "enabled", "1"].includes(status) || item.enabled === true
          ? "Active"
          : "Unknown",
  };
}

export default function CreateMailAccount() {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // small popup message (replaces toast)
  const [notice, setNotice] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showEditPasswords, setShowEditPasswords] = useState(false);

  const [actionLoading, setActionLoading] = useState("");

  // Existing MailEnable mailbox -> ERP user linking
  const [linkingAccount, setLinkingAccount] = useState(null);
  const [linkPassword, setLinkPassword] = useState("");
  const [showLinkPassword, setShowLinkPassword] = useState(false);

  const username = form.username.trim().toLowerCase();
  const email = `${username || "username"}@${DOMAIN}`;

  const showNotice = (type, text) => setNotice({ type, text });

  useEffect(() => {
    if (!notice) return undefined;

    const timer = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setMessage("");
  };

  /* ================= LIST ================= */
  const loadAccounts = useCallback(async () => {
    setListLoading(true);
    setListError("");

    try {
      const response = await api.get("/mail-accounts", {
        params: { domain: DOMAIN },
      });
      const payload = response.data;

      if (payload?.success === false) {
        throw new Error(payload.message || "Unable to load accounts.");
      }

      const records = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.accounts)
          ? payload.accounts
          : Array.isArray(payload?.data)
            ? payload.data
            : null;

      if (!records) {
        throw new Error("Unexpected account list response.");
      }

      setAccounts(
        records
          .map(normalizeAccount)
          // only softvolute.in mailboxes are shown
          .filter((account) => account.email.endsWith(`@${DOMAIN}`))
          .sort((a, b) => a.email.localeCompare(b.email)),
      );
    } catch (error) {
      setAccounts([]);
      setListError(mailError(error));
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();

    return accounts.filter((account) => {
      const matchesSearch =
        account.name.toLowerCase().includes(q) || account.email.includes(q);

      const matchesStatus =
        statusFilter === "All" || account.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [accounts, search, statusFilter]);

  const activeCount = accounts.filter((a) => a.status === "Active").length;
  const disabledCount = accounts.filter((a) => a.status === "Disabled").length;
  const linkedCount = accounts.filter((a) => a.erpLinked).length;

  /* ================= CREATE ================= */
  const clearForm = () => {
    if (loading) return;

    setForm({ ...EMPTY_FORM });
    setMessage("");
    setMessageType("");
    setShowPassword(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const name = form.name.trim();
    const mailboxName = form.username.trim().toLowerCase();
    const fullEmail = `${mailboxName}@${DOMAIN}`;

    const fail = (text) => {
      setMessage(text);
      setMessageType("error");
      showNotice("error", text);
    };

    if (!name || name.length > 100) {
      return fail("Enter a valid employee name.");
    }

    if (!MAILBOX_REGEX.test(mailboxName)) {
      return fail("Enter a valid mailbox name.");
    }

    const problem = passwordProblem(form.password);
    if (problem) return fail(problem);

    if (form.password !== form.confirmPassword) {
      return fail("Passwords do not match.");
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await api.post("/mail-accounts", {
        name,
        email: fullEmail,
        password: form.password,
      });

      if (response.data?.success !== true) {
        throw new Error(
          response.data?.message || "Account creation was not confirmed.",
        );
      }

      const successMessage =
        response.data.message || `${fullEmail} created successfully.`;

      setMessage(successMessage);
      setMessageType("success");
      showNotice("success", successMessage);

      setForm({ ...EMPTY_FORM });
      setShowPassword(false);

      await loadAccounts();
    } catch (error) {
      fail(mailError(error));
    } finally {
      setLoading(false);
    }
  };

  /* ================= LINK EXISTING MAILBOX WITH ERP ================= */
  const startLinkERP = (account) => {
    if (account.erpLinked) {
      showNotice("success", "This mailbox is already linked with ERP.");
      return;
    }

    if (account.status !== "Active") {
      showNotice("error", "Only active mailboxes can be linked.");
      return;
    }

    setLinkingAccount(account);
    setLinkPassword("");
    setShowLinkPassword(false);
  };

  const closeLinkERP = () => {
    if (actionLoading) return;

    setLinkingAccount(null);
    setLinkPassword("");
    setShowLinkPassword(false);
  };

  const linkERP = async (event) => {
    event.preventDefault();

    if (!linkingAccount || actionLoading) return;

    if (!linkPassword) {
      showNotice("error", "Enter the existing MailEnable mailbox password.");
      return;
    }

    const mailboxEmail = linkingAccount.email;
    setActionLoading(mailboxEmail);

    try {
      const response = await api.post(
        `/mail-accounts/${encodeURIComponent(mailboxEmail)}/link-erp`,
        { password: linkPassword },
      );

      if (response.data?.success !== true) {
        throw new Error(
          response.data?.message || "ERP mailbox linking was not confirmed.",
        );
      }

      showNotice(
        "success",
        response.data?.message ||
          `${mailboxEmail} linked with ERP successfully.`,
      );

      setLinkingAccount(null);
      setLinkPassword("");
      setShowLinkPassword(false);

      await loadAccounts();
    } catch (error) {
      showNotice("error", mailError(error));
    } finally {
      setActionLoading("");
    }
  };

  /* ================= EDIT ================= */
  const startEdit = (account) => {
    setEditing(account);
    setEditName(account.name === "—" ? "" : account.name);
    setOldPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setShowEditPasswords(false);
  };

  const saveEdit = async (event) => {
    event.preventDefault();

    if (!editing || !editName.trim()) return;

    setActionLoading(editing.email);

    try {
      const response = await api.patch(
        `/mail-accounts/${encodeURIComponent(editing.email)}`,
        { name: editName.trim() },
      );

      if (response.data?.success !== true) {
        throw new Error("Update was not confirmed by the backend.");
      }

      showNotice("success", "Account updated.");
      setEditing(null);

      await loadAccounts();
    } catch (error) {
      showNotice("error", mailError(error));
    } finally {
      setActionLoading("");
    }
  };

  const resetPassword = async () => {
    if (!editing) return;

    if (!oldPassword || !newPassword || newPassword !== confirmNewPassword) {
      return showNotice("error", "Enter old password and matching new passwords.");
    }

    if (newPassword === oldPassword) {
      return showNotice(
        "error",
        "New password must be different from old password.",
      );
    }

    const problem = passwordProblem(newPassword);
    if (problem) return showNotice("error", problem);

    setActionLoading(editing.email);

    try {
      const response = await api.post(
        `/mail-accounts/${encodeURIComponent(editing.email)}/reset-password`,
        {
          oldPassword,
          newPassword,
          confirmNewPassword,
        },
      );

      if (response.data?.success !== true) {
        throw new Error(
          response.data?.message || "Password reset was not confirmed.",
        );
      }

      showNotice("success", "MailEnable password changed.");

      setOldPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      showNotice("error", mailError(error));
    } finally {
      setActionLoading("");
    }
  };

  return (
    <main className="ma-page">
      {notice && (
        <div className={`ma-toast ma-toast-${notice.type}`} role="status">
          {notice.text}
        </div>
      )}

      {/* ================= HEADER ================= */}
      <header className="ma-hero">
        <div>
          <h1>Create Mail Account</h1>
          <p>Create and manage @{DOMAIN} email accounts.</p>
        </div>

        <span className="ma-domain-tag">@{DOMAIN}</span>
      </header>

      {/* ================= CREATE ================= */}
      <section className="ma-card">
        <div className="ma-card-title">
          <h2>New Email Account</h2>
          <p>Enter the details of the mailbox to create on {DOMAIN}.</p>
        </div>

        <form onSubmit={submit}>
          <div className="ma-grid">
            <label className="ma-field">
              <span>Employee Name *</span>
              <input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="e.g. Rahul Prajapati"
                maxLength={100}
                disabled={loading}
                required
              />
            </label>

            <label className="ma-field">
              <span>Mailbox Name *</span>
              <div className="ma-suffix">
                <input
                  value={form.username}
                  onChange={(e) =>
                    update("username", e.target.value.split("@")[0])
                  }
                  placeholder="e.g. rahul.prajapati"
                  maxLength={64}
                  disabled={loading}
                  required
                />
                <b>@{DOMAIN}</b>
              </div>
            </label>

            <label className="ma-field">
              <span>Post Office</span>
              <input value={DOMAIN} readOnly />
            </label>
          </div>

          <div className="ma-preview">
            <small>Email Address</small>
            <strong>{email}</strong>
          </div>

          <div className="ma-grid">
            <label className="ma-field">
              <span>Password *</span>
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                placeholder="Up to 30 characters"
                maxLength={30}
                autoComplete="new-password"
                disabled={loading}
                required
              />
            </label>

            <label className="ma-field">
              <span>Confirm Password *</span>
              <input
                type={showPassword ? "text" : "password"}
                value={form.confirmPassword}
                onChange={(e) => update("confirmPassword", e.target.value)}
                placeholder="Re-enter password"
                maxLength={30}
                autoComplete="new-password"
                disabled={loading}
                required
              />
            </label>
          </div>

          <label className="ma-check">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            />
            Show password
          </label>

          {message && (
            <div role="alert" className={`ma-message ${messageType}`}>
              {message}
            </div>
          )}

          <div className="ma-actions">
            <button
              type="button"
              className="ma-btn ma-btn-ghost"
              onClick={clearForm}
              disabled={loading}
            >
              Clear
            </button>

            <button
              type="submit"
              className="ma-btn ma-btn-primary"
              disabled={loading}
            >
              {loading ? "Processing..." : "Create Mail Account"}
            </button>
          </div>
        </form>
      </section>

      {/* ================= LIST ================= */}
      <section className="ma-card">
        <div className="ma-list-head">
          <div className="ma-card-title">
            <h2>Mail Accounts List</h2>
            <p>@{DOMAIN} mailboxes retrieved from the mail server.</p>
          </div>

          <button
            type="button"
            className="ma-btn ma-btn-ghost"
            onClick={loadAccounts}
            disabled={listLoading}
          >
            {listLoading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div className="ma-stats">
          <div>
            <small>Total Accounts</small>
            <strong>{listError ? "—" : accounts.length}</strong>
          </div>

          <div>
            <small>Active</small>
            <strong>{listError ? "—" : activeCount}</strong>
          </div>

          <div>
            <small>Disabled</small>
            <strong>{listError ? "—" : disabledCount}</strong>
          </div>

          <div>
            <small>ERP Linked</small>
            <strong>{listError ? "—" : linkedCount}</strong>
          </div>
        </div>

        <div className="ma-tools">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email ID..."
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Disabled">Disabled</option>
            <option value="Unknown">Unknown</option>
          </select>
        </div>

        {listError && (
          <div className="ma-list-error">
            Unable to load mail accounts: {listError}
          </div>
        )}

        <div className="ma-table-wrap">
          <table className="ma-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email ID</th>
                <th>Status</th>
                <th>ERP Connection</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredAccounts.map((account) => (
                <tr key={account.email}>
                  <td>{account.name}</td>
                  <td>{account.email}</td>

                  <td>
                    <span
                      className={`ma-status ${account.status.toLowerCase()}`}
                    >
                      {account.status}
                    </span>
                  </td>

                  <td>
                    {account.erpLinked ? (
                      <span className="ma-linked">ERP Linked</span>
                    ) : (
                      <span className="ma-not-linked">Not Linked</span>
                    )}
                  </td>

                  <td>
                    <div className="ma-row-actions">
                      <button
                        type="button"
                        className="ma-small-btn"
                        onClick={() => startEdit(account)}
                        disabled={!!actionLoading}
                      >
                        Edit
                      </button>

                      {!account.erpLinked && (
                        <button
                          type="button"
                          className="ma-small-btn solid"
                          onClick={() => startLinkERP(account)}
                          disabled={
                            !!actionLoading || account.status !== "Active"
                          }
                        >
                          Link ERP
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}

              {!listLoading && !listError && filteredAccounts.length === 0 && (
                <tr>
                  <td colSpan={5} className="ma-empty">
                    No mail accounts found.
                  </td>
                </tr>
              )}

              {listLoading && (
                <tr>
                  <td colSpan={5} className="ma-empty">
                    Loading mail accounts...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ================= EDIT MODAL ================= */}
      {editing && (
        <div className="ma-backdrop">
          <div
            className="ma-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Edit Mail Account"
          >
            <h2>Edit Mail Account</h2>

            <label className="ma-field">
              <span>Email ID (cannot change)</span>
              <input value={editing.email} readOnly disabled />
            </label>

            <form onSubmit={saveEdit}>
              <label className="ma-field">
                <span>Employee Name</span>
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={100}
                  required
                />
              </label>

              <div className="ma-section">
                <h3>Reset Password</h3>
                <p className="ma-modal-note">
                  Verify the current password before setting a new one.
                </p>

                <label className="ma-field">
                  <span>Old Password</span>
                  <input
                    type={showEditPasswords ? "text" : "password"}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Enter current password"
                    autoComplete="current-password"
                  />
                </label>

                <label className="ma-field">
                  <span>New Password</span>
                  <input
                    type={showEditPasswords ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Up to 30 characters"
                    maxLength={30}
                    autoComplete="new-password"
                  />
                </label>

                <label className="ma-field">
                  <span>Confirm New Password</span>
                  <input
                    type={showEditPasswords ? "text" : "password"}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                  />
                </label>

                <label className="ma-check">
                  <input
                    type="checkbox"
                    checked={showEditPasswords}
                    onChange={(e) => setShowEditPasswords(e.target.checked)}
                  />
                  Show passwords
                </label>

                <button
                  type="button"
                  className="ma-btn ma-btn-primary"
                  onClick={resetPassword}
                  disabled={!!actionLoading}
                >
                  Reset Password
                </button>
              </div>

              <div className="ma-actions">
                <button
                  type="button"
                  className="ma-btn ma-btn-ghost"
                  onClick={() => setEditing(null)}
                  disabled={!!actionLoading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="ma-btn ma-btn-primary"
                  disabled={!!actionLoading}
                >
                  {actionLoading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= LINK ERP MODAL ================= */}
      {linkingAccount && (
        <div className="ma-backdrop">
          <div
            className="ma-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Link Mailbox with ERP"
          >
            <h2>Link Mailbox with ERP</h2>

            <p className="ma-modal-note">
              Connect this existing MailEnable mailbox to the ERP employee with
              the same email address. The mailbox will not be recreated and its
              password will not be changed.
            </p>

            <form onSubmit={linkERP}>
              <label className="ma-field">
                <span>Mailbox Email</span>
                <input value={linkingAccount.email} readOnly disabled />
              </label>

              <label className="ma-field">
                <span>Existing Mailbox Password</span>
                <input
                  type={showLinkPassword ? "text" : "password"}
                  value={linkPassword}
                  onChange={(e) => setLinkPassword(e.target.value)}
                  placeholder="Enter current MailEnable password"
                  autoComplete="off"
                  required
                />
              </label>

              <label className="ma-check">
                <input
                  type="checkbox"
                  checked={showLinkPassword}
                  onChange={(e) => setShowLinkPassword(e.target.checked)}
                />
                Show password
              </label>

              <p className="ma-modal-note">
                The backend will verify the existing mailbox credentials before
                saving the encrypted ERP connection.
              </p>

              <div className="ma-actions">
                <button
                  type="button"
                  className="ma-btn ma-btn-ghost"
                  onClick={closeLinkERP}
                  disabled={!!actionLoading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="ma-btn ma-btn-primary"
                  disabled={!!actionLoading || !linkPassword}
                >
                  {actionLoading ? "Linking..." : "Link ERP"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}